import moment from "moment";

const END_TO_END_LIKE = /^[EDed][0-9A-Za-z]{20,}$/;
const GUID_LIKE =
  /^[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}$/;

const trimId = (value) => String(value ?? "").trim();

export const isStatementEndToEndId = (value) => END_TO_END_LIKE.test(trimId(value));

/**
 * Pix end-to-end (E2E) from statement / receipt payloads.
 * Prefers explicit E2E fields; also accepts operation_id / transaction_id when E2E-shaped.
 */
export const getStatementEndToEndId = (item) => {
  if (!item) return "";

  const explicit = [
    item.end_to_end_id,
    item.endToEndId,
    item.EndToEndId,
    item.end_to_end,
    item.e2e,
    item.e2e_id,
    item.trace_id,
    item.receipt?.end_to_end_id,
    item.receipt?.endToEndId,
    item.receipt?.end_to_end,
  ]
    .map(trimId)
    .find(Boolean);
  if (explicit) return explicit;

  const shaped = [item.operation_id, item.transaction_id, item.transactionId, item.id]
    .map(trimId)
    .find((value) => value && isStatementEndToEndId(value));
  return shaped || "";
};

/**
 * Internal operation id (UUID / ledger id), never the Pix E2E when both exist.
 */
export const getStatementOperationId = (item) => {
  if (!item) return "";

  const endToEnd = getStatementEndToEndId(item);
  const candidates = [item.operation_id, item.id, item.external_id, item.externalId]
    .map(trimId)
    .filter(Boolean);

  const distinct = candidates.find(
    (value) => value !== endToEnd && !isStatementEndToEndId(value),
  );
  if (distinct) return distinct;

  const op = trimId(item.operation_id);
  if (op && op !== endToEnd) return op;
  return "";
};

/** Technical tokens that must never appear as destino / subtitle. */
export const isStatementTechnicalToken = (value) => {
  const trimmed = trimId(value);
  if (!trimmed) return true;
  if (isStatementEndToEndId(trimmed)) return true;
  if (GUID_LIKE.test(trimmed)) return true;
  if (/^[A-Z][A-Z0-9_]{2,}$/.test(trimmed) && trimmed.includes("_")) return true;
  return false;
};

/** Legacy Área Pix / Extrato labels that are not a counterparty. */
export const isStatementBoilerplateLabel = (value) => {
  const trimmed = trimId(value);
  if (!trimmed) return true;
  return (
    /^PIX$/i.test(trimmed) ||
    /^PIX\s*Pagamento$/i.test(trimmed) ||
    /^PIX\s*Recebimento$/i.test(trimmed) ||
    /^Pagamento\s*PIX$/i.test(trimmed) ||
    /^Recebimento\s*PIX$/i.test(trimmed) ||
    /^Pagamento\s*Pix$/i.test(trimmed) ||
    /^Recebimento\s*Pix$/i.test(trimmed) ||
    /^Devolução\s*PIX$/i.test(trimmed) ||
    /^INTERNAL_TRANSFER$/i.test(trimmed)
  );
};

/** Drop trailing ` / KIND` (e.g. ` / PAGAMENTO`). */
export const stripStatementKindSuffix = (description) => {
  const trimmed = String(description ?? "").trim();
  if (!trimmed) return "";
  const slash = trimmed.lastIndexOf(" / ");
  if (slash <= 0) return trimmed;
  return trimmed.slice(0, slash).trim();
};

/** Product label before ` - {party}` or full left-of-kind when no party. */
export const extractProductFromDescription = (description) => {
  const left = stripStatementKindSuffix(description);
  if (!left) return "";
  if (isStatementBoilerplateLabel(left)) return "PIX";
  const dash = left.indexOf(" - ");
  if (dash < 0) return left;
  return left.slice(0, dash).trim() || "PIX";
};

/**
 * Counterparty from formatted titles: `PIX - Maria Silva / PAGAMENTO`.
 * Mirrors BankData.StatementDisplayTitle.ExtractPartyFromFormatted.
 */
export const extractDestinoFromDescription = (description) => {
  const trimmed = String(description ?? "").trim();
  if (!trimmed) return "";

  const slash = trimmed.lastIndexOf(" / ");
  if (slash <= 0) return "";

  const left = trimmed.slice(0, slash).trim();
  const dash = left.indexOf(" - ");
  if (dash < 0) return "";

  const party = left.slice(dash + 3).trim();
  if (!party || isStatementTechnicalToken(party) || isStatementBoilerplateLabel(party))
    return "";
  return party;
};

const isPixLikeItem = (item) => {
  const productId = String(item?.product_id ?? item?.category?.id ?? "").trim();
  if (productId === "149" || productId === "150") return true;
  const pixType = String(item?.pix_type ?? item?.pixType ?? "").trim();
  if (pixType) return true;
  const type = String(item?.type ?? "").trim();
  if (/^PIX$/i.test(type)) return true;
  const category = String(item?.category?.name ?? "").trim();
  if (/pix/i.test(category) || /qr\s*code/i.test(category)) return true;
  const description = String(item?.description ?? "").trim();
  if (/^PIX\b/i.test(description) || /\bPIX\b/i.test(description)) return true;
  return false;
};

const isPixReceiveItem = (item) => {
  const productId = String(item?.product_id ?? item?.category?.id ?? "").trim();
  if (productId === "150") return true;
  if (productId === "149") return false;
  const pixType = String(item?.pix_type ?? item?.pixType ?? "").toUpperCase();
  if (pixType === "RECEIVE") return true;
  if (Number(item?.amount) > 0 || item?.operation === "credit") return true;
  return false;
};

/** Human category for Extrato / Área Pix rows (never raw slug `pix`). */
export const resolveStatementCategoryLabel = (item) => {
  const raw = String(item?.category?.name ?? "").trim();
  if (/^PIX\s*PAGAMENTO$/i.test(raw) || /^Pagamento\s*Pix$/i.test(raw)) {
    return "Pagamento Pix";
  }
  if (/^PIX\s*RECEBIMENTO$/i.test(raw) || /^Recebimento\s*Pix$/i.test(raw)) {
    return "Recebimento Pix";
  }
  if (!isPixLikeItem(item)) {
    if (!raw || isStatementTechnicalToken(raw)) return "";
    return raw;
  }
  if (
    !raw ||
    /^pix$/i.test(raw) ||
    isStatementTechnicalToken(raw) ||
    isStatementBoilerplateLabel(raw)
  ) {
    return isPixReceiveItem(item) ? "Recebimento Pix" : "Pagamento Pix";
  }
  return raw;
};

/** Best available counterparty from payment/statement payload fields. */
export const extractCounterpartyFromItem = (item) => {
  if (!item || typeof item !== "object") return "";

  const candidates = [
    item.counterparty_name,
    item.counterpartyName,
    item.receiver_name,
    item.receiverName,
    item.receiver?.name,
    item.receiver?.trade_name,
    item.payer_name,
    item.payerName,
    item.payer?.name,
  ];

  for (const candidate of candidates) {
    const trimmed = String(candidate ?? "").trim();
    if (
      !trimmed ||
      isStatementTechnicalToken(trimmed) ||
      isStatementBoilerplateLabel(trimmed)
    ) {
      continue;
    }
    return trimmed;
  }
  return "";
};

/**
 * Unified transaction-row fields for Extrato / Área Pix / Início.
 * Title = product + destino; subtitle = categoria; meta = data (HH:mm).
 */
export const mapStatementTransactionRow = (item) => {
  const rawDescription = String(
    item?.description || item?.operation || "Movimentação",
  ).trim();
  const categoryName = resolveStatementCategoryLabel(item);
  const rawName = String(item?.name ?? "").trim();

  let destino = extractDestinoFromDescription(rawDescription);
  if (
    !destino &&
    rawName &&
    rawName !== rawDescription &&
    rawName !== categoryName &&
    !isStatementTechnicalToken(rawName) &&
    !isStatementBoilerplateLabel(rawName)
  ) {
    destino = rawName;
  }
  if (!destino) {
    destino = extractCounterpartyFromItem(item);
  }

  const product =
    extractProductFromDescription(rawDescription) || "Movimentação";
  const leftOfKind = stripStatementKindSuffix(rawDescription);

  let title;
  if (destino) {
    title = `${product} - ${destino}`;
  } else if (leftOfKind && !isStatementBoilerplateLabel(leftOfKind)) {
    const dash = leftOfKind.indexOf(" - ");
    if (dash >= 0) {
      const party = leftOfKind.slice(dash + 3).trim();
      title =
        isStatementTechnicalToken(party) || isStatementBoilerplateLabel(party)
          ? product
          : leftOfKind;
    } else {
      title = leftOfKind;
    }
  } else if (isStatementBoilerplateLabel(rawDescription) || isStatementBoilerplateLabel(leftOfKind)) {
    title = product;
  } else {
    title = rawDescription;
  }

  const categoria =
    categoryName && !isStatementTechnicalToken(categoryName) ? categoryName : "";
  const data = item?.created_at
    ? moment(item.created_at).format("HH:mm")
    : "";

  const memo =
    rawName &&
    rawName !== rawDescription &&
    rawName !== categoryName &&
    rawName !== destino &&
    !isStatementTechnicalToken(rawName) &&
    !isStatementBoilerplateLabel(rawName)
      ? rawName
      : "";

  return {
    title,
    destino,
    categoria,
    data,
    memo,
    /** Category line under title (never destino / kind / operation_id). */
    subtitle: categoria,
    /** Date/time only — do not repeat destino or categoria. */
    meta: data,
  };
};
